import { Component, ElementRef, HostListener, OnDestroy, OnInit, QueryList, ViewChildren } from '@angular/core'
import { CommonModule } from '@angular/common'
import { Router } from '@angular/router'
import { CompanyInfo, CompanyService } from '../../../services/company.service'
import { ApiCatalogService } from '../../../api/api-catalog.service'

type SearchTab = 'corporate-number' | 'prefecture'

const DIGIT_COUNT = 13

/**
 * クリップボードの状態
 * - 'unknown'     : まだ読み取り試行していない（権限確認前）
 * - 'no-access'   : 権限なし・HTTPS非対応などで読み取れない
 * - 'no-digits'   : 読み取れたが数字がゼロ桁
 * - 'has-digits'  : 数字が1桁以上含まれる
 */
type ClipboardState = 'unknown' | 'no-access' | 'no-digits' | 'has-digits'

@Component({
  selector: 'app-company-search',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './company-search.component.html',
  styleUrls: ['./company-search.component.scss'],
})
export class CompanySearchComponent implements OnInit, OnDestroy {
  // ─── タブ ─────────────────────────────────────────────────
  activeTab: SearchTab = 'corporate-number'

  // ─── 法人番号入力 ──────────────────────────────────────────
  digits: string[] = Array(DIGIT_COUNT).fill('')

  // ─── 検索状態 ─────────────────────────────────────────────
  isLoading = false
  results: CompanyInfo[] = []
  errorMessage = ''
  searched = false

  // ─── クリップボード ────────────────────────────────────────
  /** クリップボードから抽出した数字文字列（null = アクセス不可） */
  clipboardDigits: string | null = null
  clipboardState: ClipboardState = 'unknown'

  @ViewChildren('digitInput') digitInputs!: QueryList<ElementRef<HTMLInputElement>>

  constructor(
    private router: Router,
    private companyService: CompanyService,
    private apiCatalogService: ApiCatalogService,
  ) {}

  ngOnInit(): void {
    this.refreshClipboardPreview()
  }

  ngOnDestroy(): void {}

  /** ウィンドウがフォーカスされた時にクリップボードを再読み取り */
  @HostListener('window:focus')
  onWindowFocus(): void {
    this.refreshClipboardPreview()
  }

  // ─── クリップボード読み取り ────────────────────────────────

  /**
   * クリップボードを読み取り、数字のみを抽出してプレビュー用文字列を更新する。
   *
   * 状態遷移：
   *   権限なし / HTTPS 以外  → 'no-access'  （ボタンは汎用ラベルで有効）
   *   数字ゼロ桁             → 'no-digits'  （ボタン disabled）
   *   数字 1 桁以上          → 'has-digits' （ボタン有効・数字プレビュー表示）
   */
  async refreshClipboardPreview(): Promise<void> {
    if (!navigator.clipboard?.readText) {
      this.clipboardState = 'no-access'
      return
    }
    try {
      const raw = await navigator.clipboard.readText()
      const digits = raw.replace(/[^0-9]/g, '').slice(0, DIGIT_COUNT)
      if (digits.length === 0) {
        this.clipboardDigits = null
        this.clipboardState = 'no-digits'
      } else {
        this.clipboardDigits = digits
        this.clipboardState = 'has-digits'
      }
    } catch {
      // 権限拒否・非 HTTPS 環境など
      this.clipboardDigits = null
      this.clipboardState = 'no-access'
    }
  }

  /** ペーストボタンのラベルを返す */
  get pasteButtonLabel(): string {
    switch (this.clipboardState) {
      case 'has-digits':
        return `${this.clipboardDigits} をペースト`
      case 'no-digits':
        return '数字をコピーしてペースト'
      case 'no-access':
      default:
        return 'クリップボードからペースト'
    }
  }

  /** ペーストボタンを無効化するか */
  get isPasteDisabled(): boolean {
    return this.clipboardState === 'no-digits'
  }

  // ─── タブ切り替え ───────────────────────────────────────────
  selectTab(tab: SearchTab): void {
    this.activeTab = tab
    this.resetSearch()
  }

  // ─── 入力ハンドラ ───────────────────────────────────────────
  onInput(event: Event, index: number): void {
    const input = event.target as HTMLInputElement
    const value = input.value.replace(/[^0-9]/g, '')

    this.digits[index] = value ? value[value.length - 1] : ''
    input.value = this.digits[index]

    if (this.digits[index]) {
      this.focusNext(index)
    }

    this.checkAndSearch()
  }

  onKeydown(event: KeyboardEvent, index: number): void {
    switch (event.key) {
      case 'Backspace':
        if (!this.digits[index] && index > 0) {
          this.digits[index - 1] = ''
          this.focusAt(index - 1)
          event.preventDefault()
        } else if (this.digits[index]) {
          this.digits[index] = ''
          this.results = []
          this.searched = false
        }
        break
      case 'ArrowLeft':
        if (index > 0) this.focusAt(index - 1)
        event.preventDefault()
        break
      case 'ArrowRight':
        if (index < DIGIT_COUNT - 1) this.focusAt(index + 1)
        event.preventDefault()
        break
    }
  }

  onPaste(event: ClipboardEvent, startIndex: number): void {
    event.preventDefault()
    const pasted = (event.clipboardData?.getData('text') ?? '')
      .replace(/[^0-9]/g, '')
      .slice(0, DIGIT_COUNT - startIndex)

    for (let i = 0; i < pasted.length; i++) {
      if (startIndex + i < DIGIT_COUNT) {
        this.digits[startIndex + i] = pasted[i]
      }
    }

    const nextFocus = Math.min(startIndex + pasted.length, DIGIT_COUNT - 1)
    setTimeout(() => this.focusAt(nextFocus))
    this.checkAndSearch()
  }

  async pasteFromClipboard(): Promise<void> {
    // 'no-access' の場合は Clipboard API が使えないので何もしない（disabled でないため）
    if (this.clipboardState === 'no-digits') return

    try {
      const raw = await navigator.clipboard.readText()
      const digits = raw.replace(/[^0-9]/g, '').slice(0, DIGIT_COUNT)

      if (!digits) {
        // ボタン押下時に状態を再確認
        await this.refreshClipboardPreview()
        return
      }

      for (let i = 0; i < DIGIT_COUNT; i++) {
        this.digits[i] = digits[i] ?? ''
      }

      const nextFocus = Math.min(digits.length, DIGIT_COUNT - 1)
      setTimeout(() => this.focusAt(nextFocus))
      this.checkAndSearch()
      await this.refreshClipboardPreview()
    } catch {
      this.clipboardState = 'no-access'
    }
  }
  // ─── 検索ロジック ───────────────────────────────────────────
  private checkAndSearch(): void {
    if (this.digits.every((d) => d !== '')) {
      this.searchByCorporateNumber(this.digits.join(''))
    } else if (this.results.length > 0) {
      this.results = []
      this.searched = false
    }
  }

  private async searchByCorporateNumber(corporateNumber: string): Promise<void> {
    this.isLoading = true
    this.errorMessage = ''
    this.results = []
    this.searched = false

    let ret = await this.apiCatalogService.postSearchAllCorporate(Number(corporateNumber))
    if (ret) {
      this.results = ret
      this.isLoading = false
      this.searched = true
    } else {
      this.errorMessage = '検索中にエラーが発生しました。再度お試しください。'
      this.isLoading = false
      this.searched = true
      return
    }
  }

  resetSearch(): void {
    this.digits = Array(DIGIT_COUNT).fill('')
    this.results = []
    this.errorMessage = ''
    this.isLoading = false
    this.searched = false
  }

  // ─── 会社ボタンクリック ─────────────────────────────────────
  onCompanyClick(result: CompanyInfo): void {
    this.companyService.SelectedCompany = this.formatCompanyInfo(result)
    this.router.navigate(['/company-confirm'])
  }
  formatCompanyInfo(targetData: CompanyInfo): CompanyInfo {
    let ret = structuredClone(targetData)

    ret.invoiceNumber = ''
    ret.organizationTypeCd = 'organization_type-company'
    return ret
  }

  // ─── フォーカス管理 ─────────────────────────────────────────
  private focusNext(index: number): void {
    if (index < DIGIT_COUNT - 1) this.focusAt(index + 1)
  }

  private focusAt(index: number): void {
    const inputs = this.digitInputs?.toArray()
    inputs?.[index]?.nativeElement.focus()
    inputs?.[index]?.nativeElement.select()
  }

  get corporateNumberFilled(): boolean {
    return this.digits.every((d) => d !== '')
  }
}
